import { useEffect } from "react";
import { FileUploadCard } from "@scientist/ui";
import {
  useProposalExtraction,
  type ExtractionComplete,
  type ExtractionPhase,
} from "./useProposalExtraction";
import {
  RequestExtractionProgressCard,
  type ExtractionStatus,
} from "./RequestExtractionProgressCard";

type Props = {
  onComplete?: (result: ExtractionComplete) => void;
  serviceSelected?: boolean;
  applyFired?: boolean;
  /** Fires on every extraction-phase change. `onComplete` only reports the
   *  happy ending — surfaces outside the card that track the read from its
   *  first byte need the earlier phases and the error too. */
  onPhaseChange?: (phase: ExtractionPhase) => void;
};

function displayStatus(
  phase: ExtractionPhase,
  serviceSelected: boolean,
  applyFired: boolean,
): ExtractionStatus | "idle" | "error" {
  switch (phase) {
    case "idle": return "idle";
    case "error": return "error";
    case "uploading": return "uploading";
    case "scanning": return "scanning";
    case "ready":
      if (applyFired) return "mapping";
      if (serviceSelected) return "extracting";
      return "identifying";
  }
}

export function ProposalUploadCard({
  onComplete,
  serviceSelected = false,
  applyFired = false,
  onPhaseChange,
}: Props) {
  const { phase, fileName, errorMessage, onFileSelected } =
    useProposalExtraction({ onComplete });
  const status = displayStatus(phase, serviceSelected, applyFired);

  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  if (status === "idle" || status === "error") {
    return (
      <FileUploadCard
        label="Upload a file"
        status={status === "error" ? "error" : "idle"}
        errorMessage={errorMessage}
        onFileSelected={onFileSelected}
      />
    );
  }
  return <RequestExtractionProgressCard status={status} fileName={fileName} />;
}
